import { useTranslations } from 'next-intl';
import {
  HeaderSearchBarSuggestion,
  HeaderSearchBarSuggestionList,
  HeaderSearchBarSuggestionsColumn,
  HeaderSearchBarSuggestionsContainer,
  HeaderSearchBarSuggestionsSeparator
} from 'components/pcc-components';
import { useCallback } from 'react';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { Image } from 'components/image';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import styles from './redesigned-suggestion-list.module.scss';
import {
  suggestedCategory,
  suggestedDiagram,
  suggestedKeyword,
  suggestedProducts
} from 'services/searchSuggestions/types';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { HEADLESS_SEARCH_PAGE } from 'constants/links';
import { RESPONSE_FILTER } from 'constants/common';

type RedesignedSuggestionListProps = {
  searchValue: string;
  suggestedKeyWords: Array<suggestedKeyword>;
  suggestedProducts: Array<suggestedProducts>;
  suggestedCategories: Array<suggestedCategory>;
  suggestedDiagrams: Array<suggestedDiagram>;
  isHeadlessCategoryEnabled: boolean;
  serialNumber: string;
  model: string;
};

export const RedesignedSuggestionList = ({
  searchValue,
  suggestedKeyWords,
  suggestedProducts,
  suggestedCategories,
  suggestedDiagrams,
  isHeadlessCategoryEnabled,
  serialNumber,
  model
}: RedesignedSuggestionListProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const params = useParams();
  const seoUrl = getSeoUrl(params);

  const keywordLabel = t('SUGGESTED_KEYWORDS');
  const productLabel = t('PRODUCTS');
  const categoryLabel = t('CAT_SHOP_ALL_CATEGORIES');
  const diagramLabel = t('CAT_PARTS_DIAGRAM');

  const hasKeyWordsOrCategories =
    !isEmpty(suggestedKeyWords) || !isEmpty(suggestedCategories);

  const hasProductsOrDiagrams =
    !isEmpty(suggestedProducts) || !isEmpty(suggestedDiagrams);

  const hasKeywordsAndCategories =
    !isEmpty(suggestedKeyWords) && !isEmpty(suggestedCategories);

  const hasProductsAndDiagrams =
    !isEmpty(suggestedProducts) && !isEmpty(suggestedDiagrams);

  const basePath = replaceTokensInString(HEADLESS_SEARCH_PAGE, searchValue);

  const productParams = new URLSearchParams({
    p_page: '1',
    ...(serialNumber && { serialNumber }),
    ...(model && { model, filterByEquipment: 'true' })
  });
  const diagramParams = new URLSearchParams({
    d_page: '1',
    responseFilter: RESPONSE_FILTER.DIAGRAM,
    ...(serialNumber && { serialNumber }),
    ...(model && { model, filterByEquipment: 'true' })
  });

  const productURL = `${seoUrl}${basePath}&${productParams.toString()}`;
  const diagramsURL = `${seoUrl}${basePath}&${diagramParams.toString()}`;

  const trackItemSelection = useCallback(
    (displayName: string) => {
      fireEvent({
        formName: 'search',
        formContent: searchValue,
        formContent2: displayName
      });
    },
    [fireEvent, searchValue]
  );
  return (
    <HeaderSearchBarSuggestionList>
      <HeaderSearchBarSuggestionsContainer
        displayThreeColumns={hasKeyWordsOrCategories}
      >
        <HeaderSearchBarSuggestionsColumn
          displayColumn={hasKeyWordsOrCategories}
          className={styles['column-one']}
        >
          <div className={hasKeywordsAndCategories ? 'pb-md-4' : ''}>
            <HeaderSearchBarSuggestion
              label={keywordLabel}
              items={suggestedKeyWords}
              onItemClick={trackItemSelection}
              highlightText={searchValue}
              isFederatedSearchEnabled
              listItemClassName={
                styles['redesigned-suggestion-list-item__keywords--padding']
              }
            />
          </div>
          <div
            className={`${styles['redesigned-suggestion-list-item__category--last-item']} text-truncate`}
          >
            <HeaderSearchBarSuggestion
              label={categoryLabel}
              items={suggestedCategories}
              onItemClick={trackItemSelection}
              highlightText={searchValue}
              isHeadlessCategoryEnabled={isHeadlessCategoryEnabled}
              seoURL={seoUrl}
              isFederatedSearchEnabled
              listItemClassName={
                styles['redesigned-suggestion-list-item__category--padding']
              }
            />
          </div>
        </HeaderSearchBarSuggestionsColumn>
        <HeaderSearchBarSuggestionsSeparator
          displaySeparator={hasKeyWordsOrCategories}
        />
        <HeaderSearchBarSuggestionsColumn
          displayColumn={hasProductsOrDiagrams}
          className={hasKeyWordsOrCategories ? styles['column-three'] : ''}
        >
          <div className={hasProductsAndDiagrams ? 'pb-md-4' : ''}>
            <HeaderSearchBarSuggestion
              label={productLabel}
              items={suggestedProducts}
              onItemClick={trackItemSelection}
              highlightText={searchValue}
              imgComp={Image}
              isFederatedSearchEnabled
              displayArrow
              labelProps={{ isStatic: false, href: productURL }}
            />
          </div>
          <div>
            <HeaderSearchBarSuggestion
              label={diagramLabel}
              items={suggestedDiagrams}
              onItemClick={trackItemSelection}
              highlightText={searchValue}
              isFederatedSearchEnabled
              listItemClassName={
                styles['redesigned-suggestion-list-item__diagrams--padding']
              }
              displayArrow
              labelProps={{ isStatic: false, href: diagramsURL }}
              displayPartsDiagramIcon
            />
          </div>
        </HeaderSearchBarSuggestionsColumn>
      </HeaderSearchBarSuggestionsContainer>
    </HeaderSearchBarSuggestionList>
  );
};
